/**
 * useFormBuilderState - Custom hook encapsulating all form builder state management.
 *
 * Extracted from FormBuilder.tsx to keep the component under 400 lines.
 */

import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  useControlConfigs,
  useCreateForm,
  useForm as useFormQuery,
  useFormTypes,
  useUpdateForm,
} from '../../hooks/useFormBuilder';
import {
  buildTemplatesFromConfigs,
  createControl,
  createEmptyFormDefinition,
  createSection,
  insertControlInSection,
  moveControlInSection,
  normalizeFormDefinition,
  removeControlFromSection,
  syncFormControlLinks,
  validateFormDefinition,
  withUpdatedSection,
} from './builder-utils';
import type {
  FieldTemplate,
  FormDefinition,
  FormTypeOption,
  PendingDelete,
  PropertiesTarget,
  SelectedTarget,
} from './types';
import { useFormHistory } from './useFormHistory';

type Params = { id?: string };

export function useFormBuilderState() {
  const params = useParams<Params>();
  const navigate = useNavigate();
  const formId = params.id ? Number(params.id) : null;

  const { data: formData } = useFormQuery(formId);
  const { data: configData } = useControlConfigs();
  const { data: typeData } = useFormTypes();
  const createMutation = useCreateForm();
  const updateMutation = useUpdateForm();

  const [activeView, setActiveView] = useState<'builder' | 'preview'>('builder');
  const [libraryCollapsed, setLibraryCollapsed] = useState(false);
  const [selectedCanvas, setSelectedCanvas] = useState<SelectedTarget | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<PropertiesTarget>({ type: 'form' });
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);

  const { present, set, reset, undo, redo, canUndo, canRedo } = useFormHistory<FormDefinition>(
    createEmptyFormDefinition(),
  );

  const form = present || createEmptyFormDefinition();
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const templates = useMemo(
    () => buildTemplatesFromConfigs(configData?.values || []),
    [configData],
  );

  const formTypeOptions: FormTypeOption[] = useMemo(
    () => (typeData?.values || []).map((item) => ({ value: item.id, label: item.name })),
    [typeData],
  );

  // Load form from API when editing
  const hasLoaded = useRef(false);
  useEffect(() => {
    if (formData?.values && !hasLoaded.current) {
      hasLoaded.current = true;
      reset(normalizeFormDefinition(formData.values));
    }
  }, [formData, reset]);

  const handleAddSection = useCallback(() => {
    const current = formRef.current;
    const nextSection = createSection(
      `Section ${current.sections.length + 1}`,
      current.sections,
      current.created_by,
    );
    set({ ...current, sections: [...current.sections, nextSection] });
    setSelectedCanvas({ sectionIndex: current.sections.length, controlIndex: null });
    setSelectedTarget({ type: 'section', sectionIndex: current.sections.length });
  }, [set]);

  const handleAddControl = useCallback(
    (sectionIndex: number, template: FieldTemplate, position?: number) => {
      const current = formRef.current;
      if (!current.sections[sectionIndex]) return;
      const nextControl = createControl(
        template.type,
        template.label,
        current.sections,
        template.defaults,
      );
      const nextForm = insertControlInSection(current, sectionIndex, nextControl, position);
      set(nextForm);
      const insertedIndex =
        position !== undefined &&
        position >= 0 &&
        position <= current.sections[sectionIndex].controls.length
          ? position
          : nextForm.sections[sectionIndex].controls.length - 1;
      setSelectedCanvas({ sectionIndex, controlIndex: insertedIndex });
      setSelectedTarget({ type: 'control', sectionIndex, controlIndex: insertedIndex });
    },
    [set],
  );

  const handleAddFromLibrary = useCallback(
    (template: FieldTemplate) => {
      let current = formRef.current;
      let targetSection = selectedCanvas?.sectionIndex ?? 0;
      if (current.sections.length === 0) {
        const newSection = createSection('Section 1', current.sections, current.created_by);
        current = { ...current, sections: [newSection] };
        targetSection = 0;
      }
      const nextControl = createControl(
        template.type,
        template.label,
        current.sections,
        template.defaults,
      );
      const nextForm = insertControlInSection(current, targetSection, nextControl);
      set(nextForm);
      const idx = nextForm.sections[targetSection].controls.length - 1;
      setSelectedCanvas({ sectionIndex: targetSection, controlIndex: idx });
      setSelectedTarget({ type: 'control', sectionIndex: targetSection, controlIndex: idx });
    },
    [set, selectedCanvas],
  );

  const handleMoveControl = useCallback(
    (sectionIndex: number, fromIndex: number, toIndex: number) => {
      set(moveControlInSection(formRef.current, sectionIndex, fromIndex, toIndex));
    },
    [set],
  );

  const handleMoveControlToSection = useCallback(
    (fromSection: number, fromIndex: number, toSection: number) => {
      const current = formRef.current;
      const movedControl = current.sections[fromSection]?.controls[fromIndex];
      if (!movedControl) return;
      let nextForm = removeControlFromSection(current, fromSection, fromIndex);
      nextForm = insertControlInSection(nextForm, toSection, movedControl);
      set(nextForm);
    },
    [set],
  );

  const handleDuplicateSection = useCallback(
    (sectionIndex: number) => {
      const current = formRef.current;
      const source = current.sections[sectionIndex];
      if (!source) return;
      const numericIds = current.sections
        .flatMap((s) => s.controls)
        .map((c) => parseInt(String(c.q_id), 10))
        .filter((v) => !Number.isNaN(v));
      let nextId = numericIds.length ? Math.max(...numericIds) + 1 : 1000;
      const newSection = createSection(
        `${source.section_name} (copy)`,
        current.sections,
        current.created_by,
      );
      newSection.section_description = source.section_description;
      newSection.controls = (source.controls || []).map((c) => ({
        ...c,
        q_id: String(nextId++),
        value: null,
        prev_q_id: null,
        next_q_id: null,
      }));
      const nextSections = [...current.sections];
      nextSections.splice(sectionIndex + 1, 0, newSection);
      set(syncFormControlLinks({ ...current, sections: nextSections }));
    },
    [set],
  );

  const handleRenameSection = useCallback(
    (sectionIndex: number, name: string) => {
      set(withUpdatedSection(formRef.current, sectionIndex, (s) => ({ ...s, section_name: name })));
    },
    [set],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!pendingDelete) return;
    const current = formRef.current;
    if (pendingDelete.type === 'section') {
      set({
        ...current,
        sections: current.sections.filter((_, idx) => idx !== pendingDelete.sectionIndex),
      });
      setSelectedTarget({ type: 'form' });
      setSelectedCanvas(null);
    }
    if (pendingDelete.type === 'control') {
      set(
        removeControlFromSection(current, pendingDelete.sectionIndex, pendingDelete.controlIndex),
      );
      setSelectedTarget({ type: 'section', sectionIndex: pendingDelete.sectionIndex });
      setSelectedCanvas({ sectionIndex: pendingDelete.sectionIndex, controlIndex: null });
    }
    setPendingDelete(null);
  }, [pendingDelete, set]);

  const handleSave = useCallback(async () => {
    const current = formRef.current;
    const issues = validateFormDefinition(current);
    if (issues.length) {
      showErrorToast(issues[0].message);
      return;
    }
    const payload = normalizeFormDefinition(
      syncFormControlLinks({
        ...current,
        sections: current.sections.map((section, index) => ({ ...section, sequence: index + 1 })),
      }),
    );
    try {
      if (current.id) {
        await updateMutation.mutateAsync({ id: current.id, data: payload });
        showSuccessToast('Form updates have been saved.');
      } else {
        const result = await createMutation.mutateAsync(payload);
        showSuccessToast('Form created successfully.');
        if (result?.values) navigate(`/settings/form-builder/${result.values}`);
      }
    } catch {
      showErrorToast('Unable to save form. Please try again.');
    }
  }, [createMutation, updateMutation, navigate]);

  const selectedSection = selectedCanvas ? form.sections[selectedCanvas.sectionIndex] : null;
  const selectedControl =
    selectedCanvas && selectedCanvas.controlIndex !== null
      ? form.sections[selectedCanvas.sectionIndex]?.controls[selectedCanvas.controlIndex]
      : null;

  return {
    form,
    set,
    undo,
    redo,
    canUndo,
    canRedo,
    navigate,
    activeView,
    setActiveView,
    libraryCollapsed,
    setLibraryCollapsed,
    selectedCanvas,
    setSelectedCanvas,
    selectedTarget,
    setSelectedTarget,
    pendingDelete,
    setPendingDelete,
    templates,
    formTypeOptions,
    selectedSection,
    selectedControl,
    isSaving: createMutation.isPending || updateMutation.isPending,
    handleAddSection,
    handleAddControl,
    handleAddFromLibrary,
    handleMoveControl,
    handleMoveControlToSection,
    handleDuplicateSection,
    handleRenameSection,
    handleDeleteConfirm,
    handleSave,
  };
}
